/**
 * CHROMARBLE MATCH game rules.
 *
 * Every rule that affects board evolution or scoring lives here and is versioned by
 * RULES_VERSION. A match stores the version it was created with; the server refuses to
 * process moves for a match whose version differs from the running engine, so a deploy
 * can never silently change the outcome of a match in progress.
 */
export const RULES_VERSION = 'cm-1.0.0';

export interface PuzzleRules {
  /** Board height in cells. */
  rows: number;
  /** Board width in cells. */
  cols: number;
  /** Number of distinct block types (colors). Types are 0..blockTypes-1. */
  blockTypes: number;
  /** Minimum run length that clears. */
  minMatch: number;
  /** Points per cleared block beyond (minMatch - 1): 100 x (unique cleared - 2). */
  pointsPerBlock: number;
  /**
   * Chain multipliers stored x2 so every score stays an integer:
   * chain 1 = 1.0x, 2 = 1.5x, 3 = 2.0x, 4 = 2.5x, 5+ = 3.0x.
   */
  chainMultipliersX2: readonly number[];
  /** Attempts at reshuffling a dead board before generating a fresh one. */
  maxShuffleAttempts: number;
  /** Attempts at generating a valid initial board before giving up (should never happen). */
  maxGenerateAttempts: number;
}

export const PUZZLE_RULES: PuzzleRules = Object.freeze({
  rows: 7,
  cols: 7,
  blockTypes: 6,
  minMatch: 3,
  pointsPerBlock: 100,
  chainMultipliersX2: Object.freeze([2, 3, 4, 5, 6]),
  maxShuffleAttempts: 200,
  maxGenerateAttempts: 1000,
});

export interface MatchRules {
  /** Playing time once the match starts. */
  durationMs: number;
  /** Visible 3-2-1 countdown before the start. */
  countdownMs: number;
  /**
   * Extra lead time added on top of the countdown so the MATCH_STARTING broadcast
   * reaches every client before the countdown begins.
   */
  startBufferMs: number;
  /**
   * Moves received by the server up to this long after ends_at are still accepted.
   * Covers the network trip for a swap the player made before the visible timer hit zero.
   * Anything later is rejected with MATCH_TIME_OVER.
   */
  inputGraceMs: number;
  /** After ends_at + finalizeDelayMs the match is finalized (lazily or by the sweeper). */
  finalizeDelayMs: number;
  minPlayers: number;
  maxPlayers: number;
  /** Rooms (and their invite links) expire this long after creation or last rematch. */
  roomTtlMs: number;
}

export const MATCH_RULES: MatchRules = Object.freeze({
  durationMs: 60_000,
  countdownMs: 3_000,
  startBufferMs: 1_000,
  inputGraceMs: 300,
  finalizeDelayMs: 1_000,
  minPlayers: 2,
  maxPlayers: 8,
  roomTtlMs: 2 * 60 * 60 * 1000,
});
